import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Droplets, Car, Sofa, ArrowRight, ShieldCheck, BarChart3, Boxes } from "lucide-react";

export default function Landing() {
  const tenants = [
    { slug: "aquapure", name: "AquaPure Water", icon: Droplets, color: "#0284C7", bg: "#F0F9FF", desc: "Water purifier rentals — Hyderabad" },
    { slug: "drivex", name: "DriveX Rentals", icon: Car, color: "#DC2626", bg: "#0A0A0A", desc: "Cars & bikes on hourly/daily rent — Bengaluru" },
    { slug: "kasturi", name: "Kasturi Furniture", icon: Sofa, color: "#78350F", bg: "#FAFAF9", desc: "Home & office furniture rentals — Pune" },
  ];
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top nav */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo-link">
            <div className="w-8 h-8 rounded bg-slate-900 grid place-items-center text-white font-bold">R</div>
            <span className="font-display text-xl font-bold tracking-tight">Rental<span className="text-blue-600">.SaaS</span></span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link to="/admin/login" data-testid="landing-admin-login-link" className="text-sm text-slate-700 hover:text-slate-900 px-3 py-1.5">App Owner</Link>
            <Link to="/tenant/aquapure/admin/login" data-testid="landing-tenant-demo-link" className="text-sm text-slate-700 hover:text-slate-900 px-3 py-1.5">Tenant Demo</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative grid-bg">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold tracking-wide" data-testid="landing-hero-badge">
              MULTI-TENANT • WHITE-LABEL • MARKETPLACE
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 text-slate-900">
              The complete rental<br />
              <span className="text-blue-600">SaaS platform</span> for India.
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              Launch your own branded rental PWA in minutes. Manage products, sellers, recurring rentals,
              service tickets, leads & rewards — all from one clean dashboard built for small businesses.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="landing-start-tenant-btn" size="lg" className="bg-slate-900 hover:bg-slate-800">
                <Link to="/tenant/aquapure/admin/login">Try Tenant Dashboard <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild data-testid="landing-superadmin-btn" size="lg" variant="outline">
                <Link to="/admin/login">Super Admin Login</Link>
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Phone OTP secure</div>
              <div className="flex items-center gap-1.5"><BarChart3 className="h-4 w-4 text-blue-600" /> Built-in analytics</div>
              <div className="flex items-center gap-1.5"><Boxes className="h-4 w-4 text-amber-600" /> Marketplace ready</div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-white p-3">
              <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1200&q=80" alt="dashboard" className="w-full h-full object-cover rounded-lg" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block">
              <Card className="p-4 w-56 shadow-md">
                <p className="text-xs text-slate-500">Active Tenants</p>
                <p className="text-3xl font-display font-bold">312</p>
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-2">
                  <div className="h-full w-3/4 bg-emerald-500" />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Live tenant storefronts */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Three brands. One platform.</h2>
              <p className="text-slate-500 mt-2">Every tenant gets a unique storefront — own theme, fonts, and feel.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {tenants.map((t) => {
              const Icon = t.icon;
              return (
                <Link
                  key={t.slug}
                  to={`/t/${t.slug}`}
                  data-testid={`landing-storefront-${t.slug}-link`}
                  className="group relative rounded-2xl overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow"
                  style={{ background: t.bg }}
                >
                  <div className="p-6 h-56 flex flex-col justify-between" style={{ color: t.bg === "#0A0A0A" ? "#fff" : "#0f172a" }}>
                    <Icon className="h-8 w-8" style={{ color: t.color }} />
                    <div>
                      <p className="text-xs uppercase tracking-widest opacity-60">/t/{t.slug}</p>
                      <h3 className="text-2xl font-bold mt-1" style={{ color: t.color }}>{t.name}</h3>
                      <p className="text-sm mt-1 opacity-80">{t.desc}</p>
                      <div className="mt-3 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: t.color }}>
                        Visit storefront <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Everything for a modern rental business</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {[
              ["Branded PWA", "Each tenant gets a white-label storefront — themes, colors, fonts, banners, offers."],
              ["Flexible rentals", "Hourly, daily, weekly, monthly, yearly, custom date range — even recurring billing."],
              ["Marketplace", "Onboard sellers, set per-seller commission, automate settlements & payouts."],
              ["Service tickets", "Customers raise complaints, technicians close them with photos & OTP."],
              ["Rewards engine", "Per-tenant referral & loyalty points configurable in seconds."],
              ["Reports", "Live revenue, overdue, deposits, leads, tickets — at-a-glance dashboards."],
            ].map(([title, desc], i) => (
              <Card key={i} className="p-6 border border-slate-200 bg-white" data-testid={`landing-feature-${i}`}>
                <div className="text-xs font-mono text-slate-400">0{i + 1}</div>
                <h3 className="font-display text-lg font-semibold mt-2">{title}</h3>
                <p className="text-sm text-slate-600 mt-2">{desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-500 flex justify-between">
          <span>© Rental.SaaS Platform</span>
          <span>Built for Bharat 🇮🇳</span>
        </div>
      </footer>
    </div>
  );
}
